import { Droplets, Utensils, Moon, Flame, Thermometer, Wind, type LucideIcon } from "lucide-react";
import { storyElement } from "@/lib/story-element";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";

interface SurvivalWidgetProps {
  hunger?: number | null;
  thirst?: number | null;
  rest?: number | null;
  compact?: boolean;
  // Dynamic needs from state — keys like need_warmth, need_oxygen etc.
  [key: string]: any;
}

const KNOWN_NEEDS: Record<string, { label: string; icon: LucideIcon; color: string; low: string }> = {
  hunger: { label: "Hunger", icon: Utensils, color: "bg-amber-500", low: "text-amber-400" },
  thirst: { label: "Thirst", icon: Droplets, color: "bg-blue-500", low: "text-blue-400" },
  rest: { label: "Rest", icon: Moon, color: "bg-violet-500", low: "text-violet-400" },
  warmth: { label: "Warmth", icon: Thermometer, color: "bg-orange-500", low: "text-orange-400" },
  oxygen: { label: "Oxygen", icon: Wind, color: "bg-cyan-500", low: "text-cyan-400" },
};

const DEFAULT_NEED = { icon: Flame, color: "bg-rose-500", low: "text-rose-400" };

function statusLabel(val: number): string {
  if (val > 75) return "Good";
  if (val > 50) return "OK";
  if (val > 25) return "Low";
  if (val > 10) return "Critical";
  return "Failing";
}

export function SurvivalWidget(props: SurvivalWidgetProps) {
  const { compact, ...rest } = props;

  // Collect all need values — both explicit props and dynamic state keys
  const needs: Array<{ key: string; label: string; icon: LucideIcon; color: string; low: string; value: number }> = [];

  // Check all props for need_ prefixed keys or known need names
  for (const [key, val] of Object.entries(rest)) {
    if (val == null || typeof val !== "number") continue;
    // Direct known needs (hunger, thirst, rest)
    if (KNOWN_NEEDS[key]) {
      needs.push({ key, ...KNOWN_NEEDS[key], value: val });
    }
  }

  // Sort by known order, then alphabetical for custom
  const knownOrder = ["hunger", "thirst", "rest", "warmth", "oxygen"];
  needs.sort((a, b) => {
    const ai = knownOrder.indexOf(a.key);
    const bi = knownOrder.indexOf(b.key);
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    return a.key.localeCompare(b.key);
  });

  if (needs.length === 0) {
    return (
      <div className="text-xs text-muted-foreground/50 text-center py-4">
        No survival data yet
      </div>
    );
  }

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        {needs.map((need) => {
          const Icon = need.icon;
          return (
            <div key={need.key} className="flex items-center gap-1 text-[10px]">
              <Icon className={cn("w-3 h-3", need.value < 30 ? need.low : "text-muted-foreground")} />
              <span className={cn(need.value < 30 ? "text-destructive font-medium" : "text-muted-foreground")}>
                {need.value}
              </span>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div {...storyElement("survival-widget")} className="space-y-3">
      <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">
        Survival
      </div>
      {needs.map((need) => {
        const Icon = need.icon;
        const isLow = need.value < 30;
        return (
          <div key={need.key} className="space-y-1">
            <div className="flex items-center justify-between text-xs">
              <div className={cn("flex items-center gap-1.5", isLow && "text-destructive")}>
                <Icon className="w-3.5 h-3.5" />
                <span className="font-medium">{need.label}</span>
              </div>
              <span className={cn("text-[10px]", isLow ? "text-destructive" : "text-muted-foreground")}>
                {need.value}/100 · {statusLabel(need.value)}
              </span>
            </div>
            <Progress
              value={need.value}
              className={cn("h-2", isLow && "animate-pulse")}
            />
          </div>
        );
      })}
    </div>
  );
}
